import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CircularProgress, Box, Typography, Button, IconButton, Tooltip, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { Done, CloudUploadOutlined } from "@mui/icons-material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import { useDropzone } from "react-dropzone";
import useNotification from "../../hooks/useNotification";
import useDrive from "../../hooks/useDrive";
import useCalculator from "../../hooks/useCalculator";
import { LAYOUTS, UPLOAD_STATUS } from "../../config/appConst";
import DriveActionBar from "../DriveActionBar";
import FileUpload from "../FileUpload";
import { FolderContentGrid } from "./FolderContentGrid";
import { FolderContentList } from "./FolderContentList";
import FileBreadcrumbs from "../FileBreadCrumb";
import styles from "./fileAndFolderTbl.module.css";
import DriveService from "../../services/DriveService";
import FileService from "../../services/FileService";
import { DRIVE_TYPES } from "../../config/appConst";

export default function FilesAndFoldersTbl() {
  const { t } = useTranslation();
  const { CurrentDrive, uploadStatus, layout, changeCurrentDrive } = useDrive();
  const { byteConversion }   = useCalculator();
  const { addNotification }  = useNotification();
  const [rows, setRows]      = useState([]);
  const [droppedFile, setDroppedFile] = useState(new Blob());

  // Select mode
  const [selectMode, setSelectMode]   = useState(false);
  const [selected, setSelected]       = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);

  const onDrop = useCallback(files => { if (files[0]) setDroppedFile(files[0]); }, []);
  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop, noClick: true, multiple: false, maxFiles: 1,
  });

  useEffect(() => {
    if (fileRejections?.length) {
      try { addNotification({ type: "error", message: fileRejections[0].errors[0].message }); }
      catch { addNotification({ type: "error", message: "Something went wrong!" }); }
    }
  }, [fileRejections]);

  // Reload rows only when the actual drive data changes — NOT on every uploadStatus tick
  useEffect(() => {
    if (!CurrentDrive) return;
    const { drive, sharedDrive, driveType } = CurrentDrive;
    const source = driveType === "shared" ? sharedDrive : drive;
    if (source) loadRows(source);
    setSelectMode(false);
    setSelected(new Set());
  }, [CurrentDrive]); // eslint-disable-line react-hooks/exhaustive-deps

  // Only add/remove the ghost status row — no full reload
  useEffect(() => {
    if (!uploadStatus || uploadStatus === UPLOAD_STATUS.DONE) return;
    setRows(prev => {
      const filtered = prev.filter(r => !Object.values(UPLOAD_STATUS).includes(r.id));
      const STATUS = {
        [UPLOAD_STATUS.UPLOADING]:      { Key: t('drive.uploading'),      type: UPLOAD_STATUS.UPLOADING },
        [UPLOAD_STATUS.COMPLETING]:     { Key: t('drive.uploadComplete'),  type: UPLOAD_STATUS.COMPLETING },
        [UPLOAD_STATUS.FILE_COPPING]:   { Key: t('drive.copyingFile'),     type: UPLOAD_STATUS.FILE_COPPING },
        [UPLOAD_STATUS.FILE_MOVING]:    { Key: t('drive.movingFile'),      type: UPLOAD_STATUS.FILE_MOVING },
        [UPLOAD_STATUS.FOLDER_COPPING]: { Key: t('drive.copyingFolder'),   type: UPLOAD_STATUS.FOLDER_COPPING },
        [UPLOAD_STATUS.FOLDER_MOVING]:  { Key: t('drive.movingFolder'),    type: UPLOAD_STATUS.FOLDER_MOVING },
      };
      if (STATUS[uploadStatus]) return [...filtered, { ...STATUS[uploadStatus], LastModified: "", Size: "", id: uploadStatus }];
      return filtered;
    });
  }, [uploadStatus]);

  const loadRows = ({ Files, Folders }) => {
    if (!Folders) return;
    const r = [];
    Folders.forEach((f, i) => {
      const name = f["Prefix"].replace("/", "");
      r.push({ Key: name, LastModified: "", Size: "", id: `fo_${i}`, type: "folder" });
    });
    Files.forEach((fi, i) => {
      const name = fi["Key"].replace("/", "");
      r.push({
        path: CurrentDrive.parent ? `${CurrentDrive.parent}/${name}` : name,
        Key: name, LastModified: fi["LastModified"],
        Size: byteConversion(fi["Size"], 2), id: `fi_${i}`, type: "file",
      });
    });
    setRows(r);
  };

  // ── Select helpers ──
  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = (items) => {
    const allSel = items.every(r => selected.has(r.id));
    if (allSel) {
      setSelected(prev => { const next = new Set(prev); items.forEach(r => next.delete(r.id)); return next; });
    } else {
      setSelected(prev => { const next = new Set(prev); items.forEach(r => next.add(r.id)); return next; });
    }
  };

  const exitSelectMode = () => { setSelectMode(false); setSelected(new Set()); };

  const handleDeleteSelected = async () => {
    const { parent, driveType } = CurrentDrive;
    const toDelete = rows.filter(r => selected.has(r.id));
    for (const row of toDelete) {
      const path = parent && parent !== "" ? `${parent}/${row.Key}` : row.Key;
      if (row.type === "folder") await DriveService.delete(path, driveType);
      else await FileService.delete(path, driveType);
    }
    addNotification({ type: "success", message: t('drive.itemsDeleted', { count: toDelete.length }) });
    const drive = await DriveService.getFileTree(parent, driveType);
    if (driveType === DRIVE_TYPES.SHARED) {
      changeCurrentDrive({ parent, drive: {}, sharedDrive: drive, driveType });
    } else {
      changeCurrentDrive({ parent, drive });
    }
    setConfirmOpen(false);
    exitSelectMode();
  };

  const folders = rows.filter(r => r.type === "folder");
  const files   = rows.filter(r => r.type === "file");

  const renderSection = (items, label) => {
    if (!items.length) return null;
    return (
      <>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>{label}</span>
          <span className={styles.sectionCount}>{items.length}</span>
        </div>
        {layout === LAYOUTS.GRID
          ? <FolderContentGrid rows={items} selectMode={selectMode} selected={selected} onToggle={toggleOne} />
          : (
            <FolderContentList
              rows={items}
              selectMode={selectMode}
              selected={selected}
              onToggle={toggleOne}
              onToggleAll={toggleAll}
            />
          )
        }
      </>
    );
  };

  return (
    <div {...getRootProps()} className={styles.page}>
      <input {...getInputProps()} />

      {/* Action bar */}
      {CurrentDrive && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5, flexWrap: "wrap" }}>
          <Box sx={{ flex: 1 }}>
            <DriveActionBar />
          </Box>

          {/* Select-mode toolbar */}
          {selectMode ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: 14, minWidth: 90 }}>
                {t('drive.selected', { count: selected.size })}
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<DeleteOutlineIcon />}
                disabled={selected.size === 0}
                onClick={() => setConfirmOpen(true)}
                sx={{
                  bgcolor: "#e85878", textTransform: "none", fontWeight: 600,
                  "&:hover": { bgcolor: "#c43058" },
                  "&.Mui-disabled": { bgcolor: "rgba(232,88,120,0.2)", color: "rgba(255,255,255,0.3)" },
                }}
              >
                {t('drive.deleteCount', { count: selected.size })}
              </Button>
              <Tooltip title={t('drive.cancelSelection')}>
                <IconButton onClick={exitSelectMode} sx={{ color: "rgba(255,255,255,0.5)", "&:hover": { color: "#fff" } }}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <Tooltip title={t('drive.selectToDelete')}>
              <IconButton
                onClick={() => setSelectMode(true)}
                sx={{
                  color: "#f87171",
                  bgcolor: "rgba(248,113,113,0.08)",
                  border: "1px solid rgba(248,113,113,0.2)",
                  borderRadius: 2,
                  "&:hover": { bgcolor: "rgba(248,113,113,0.18)", borderColor: "#f87171" },
                }}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      {/* Breadcrumb — below action bar */}
      {CurrentDrive && (
        <Box sx={{ mb: 2.5 }}>
          <FileBreadcrumbs />
        </Box>
      )}

      {/* Drag overlay */}
      {isDragActive && (
        <div className={styles.dropOverlay}>
          <div className={styles.dropBox}>
            <CloudUploadOutlined className={styles.dropIcon} sx={{ fontSize: 56 }} />
            <div className={styles.dropTitle}>{t('drive.dropToUpload')}</div>
            <div className={styles.dropSub}>{t('drive.filesWillBeAdded')}</div>
          </div>
        </div>
      )}

      {renderSection(folders, t('drive.folders'))}
      {renderSection(files, t('drive.files'))}

      {rows.length === 0 && CurrentDrive && !isDragActive && (
        <div className={styles.emptyState}>
          <CloudUploadOutlined className={styles.emptyIcon} sx={{ fontSize: 64 }} />
          <div className={styles.emptyTitle}>{t('drive.emptyFolder')}</div>
          <div className={styles.emptySub}>{t('drive.dragDropHere')}</div>
        </div>
      )}

      {CurrentDrive && <FileUpload droppedFile={droppedFile} hideUploadButton />}

      {/* Bulk delete confirmation */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { bgcolor: "#1a1a3e", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 2 } }}
      >
        <DialogTitle sx={{ color: "#fff", fontWeight: 600, fontSize: 18 }}>{t('drive.deleteItems')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: 14 }}>
            {t('drive.deleteConfirm', { count: selected.size })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setConfirmOpen(false)} sx={{ color: "rgba(255,255,255,0.5)", textTransform: "none" }}>
            {t('drive.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleDeleteSelected}
            startIcon={<DeleteOutlineIcon />}
            sx={{ textTransform: "none", bgcolor: "#e85878", "&:hover": { bgcolor: "#c43058" } }}
          >
            {t('drive.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
